import { useState } from "react";
import "../styles/UrlImporter.css";
import logo_close from '../assets/close-check.png';
import ImportedObjectServices from "../services/ImportedObjectServices";
import { ImportedObject } from "../bean/ImportedObject";
import { useNotification } from '../components/NotificationProvider';

function UrlImporter({ isOpen, onClose, onResult, onManualEdit,p_url = null }) {
  const [url, setUrl] = useState(p_url || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState("input"); // input | selectImage
  const [imported, setImported] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const { addNotification } = useNotification();

  if (!isOpen) return null;

  const buildCandidates = (obj) => {
    const list = Array.isArray(obj.imageList) ? obj.imageList.filter(Boolean) : [];
    const normalized = [];
    const seen = new Set();
    const pushIfNew = (u) => {
      if (typeof u !== 'string') return;
      const s = u.trim();
      if (!s) return;
      if (!seen.has(s)) { seen.add(s); normalized.push(s); }
    };
    if (obj.image) pushIfNew(obj.image);
    list.forEach(pushIfNew);
    return normalized;
  };

  const hasMeaningfulData = (obj) => {
    if (!obj) return false;
    const meaningfulTitle = typeof obj.title === "string" && obj.title.trim().toLowerCase() !== "sans titre";
    const hasDescription = typeof obj.description === "string" && obj.description.trim().length > 0;
    const hasImage = typeof obj.image === "string" && obj.image.trim().length > 0;
    const hasPrice = obj.price !== undefined && obj.price !== null && obj.price !== "" && obj.price !== 0;
    return meaningfulTitle && hasDescription && hasImage && hasPrice;
  };

  const notifyManualFallback = () => {
    // alert("Nous n’avons pas pu obtenir les informations de ce site marchand. L’accès à ses données est actuellement restreint par le site. Nous travaillons activement à améliorer cette situation. Vous pouvez néanmoins compléter les informations manuellement.");
    addNotification("Nous n’avons pas pu obtenir toutes les informations de ce site marchand. L’accès à ses données est actuellement restreint par le site. Nous travaillons activement à améliorer cette situation. Vous pouvez néanmoins compléter les informations manuellement.","",15000);
  };

  // Vérifie grossièrement qu'on a bien quelque chose qui ressemble à une URL
  const looksLikeUrl = (value) => {
    if (!value) return false;
    const v = value.trim();
    // Doit commencer par http:// ou https://
    if (!v.startsWith("http://") && !v.startsWith("https://")) return false;
    // Pas d'espaces
    if (/\s/.test(v)) return false;
    try {
      const u = new URL(v);
      // Vérifie que le hostname contient un "."
      if (!u.hostname.includes(".")) return false;
      return true;
    } catch (e) {
      return false;
    }
  };


  const handleFetch = async () => {
    if (!url) return;

    const urltrimmed = url.trim();

    if (!looksLikeUrl(urltrimmed)) {
      setError(
        <>
          Le texte saisi ne ressemble pas à une adresse web de produit.
          <br />
          Pour savoir comment copier le lien d’un article, consultez l’aide&nbsp;
          <br />
          <a href="https://listaki.fr/aide/ajouter-produit-automatique/" target="_blank" rel="noreferrer">
            Comment ajouter un article automatiquement
          </a>
          <br /> ou 
          <br /><a href="https://listaki.fr/aide/ajouter-produit-manuellement/" target="_blank" rel="noreferrer">
            Comment ajouter un article manuellement
          </a>.
        </>
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const importedObj = await ImportedObjectServices.fetchObjectFromUrl(urltrimmed);

      if (importedObj) {
        console.log("Imported object:", importedObj);
        if (!hasMeaningfulData(importedObj)) {
          notifyManualFallback();
        }
        importedObj.url = url;
        const imgs = buildCandidates(importedObj);
        if (imgs.length > 1) {
          setImported(importedObj);
          setCandidates(imgs);
          setSelectedIdx(0);
          setStep("selectImage");
        } else {
          onResult(importedObj);
          onClose();
        }
      } else {
        notifyManualFallback();
        const objet = new ImportedObject();
        objet.url = urltrimmed;
        onResult(objet);
        onClose();
      }
    } catch (err) {
      console.error(err);
      setError("Erreur lors de la récupération.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onResult(null);
    onClose();
  };

  const handleManualEdit = () => {
    onManualEdit(null);
    onClose();
  };

  const validateSelection = () => {
    if (!imported) return handleCancel();
    const chosen = candidates[selectedIdx];
    if (chosen) imported.image = chosen;
    onResult(imported);
    onClose();
  };

  return (
    <div className="ui-overlay">
      <div className="ui-modal">
        <img src={logo_close} alt="Fermer" onClick={() => handleCancel()} className='kdo-logoclose'/>
        {step === "input" ? (
          <>
            <h2>Importer un article</h2>
            <p>Copiez un lien vers la page d'un produit en ligne, nous allons essayer d'ajouter automatiquement ses caractéristiques à votre liste</p>
            <input
              type="text"
              placeholder="Collez ici l’URL de l’article"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            {error && <div className="ui-error">{error}</div>}
            <div className="ui-buttons">
              <button onClick={handleCancel}>Annuler</button>
              <button onClick={() => { if (window.umami) window.umami.track('addObject-FindObjectInfos'); handleFetch(); }} disabled={loading || !url}>
                {loading ? "Recherche..." : "Ajout Auto"}
              </button>
               ou 
               <button onClick={() => { if (window.umami) window.umami.track('addObject-ManualEdit'); handleManualEdit(); }}>Ajout manuel</button>
            </div>
          </>
        ) : (
          <>
            <h2>Sélectionnez une image</h2>
            <p>Plusieurs images ont été trouvées pour ce produit. Choisissez celle que vous préférez.</p>
            <div className="ui-image-grid">
              {candidates.map((img, idx) => (
                <button
                  key={idx}
                  className={`ui-image-thumb ${idx === selectedIdx ? 'selected' : ''}`}
                  onClick={() => setSelectedIdx(idx)}
                  title={img}
                >
                  {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                  <img src={img} alt={`Image ${idx + 1}`} />
                </button>
              ))}
            </div>
            <div className="ui-buttons">
              <button onClick={() => { setStep('input'); setImported(null); setCandidates([]); }}>Retour</button>
              <button onClick={validateSelection}>Valider</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default UrlImporter;

